import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Send, X, Loader2, Bot, User, Wand2, ChevronRight, FileText,
  CheckCircle2, Gauge, Flame, Flag, Crosshair, Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import { CommanServices } from '@/services/commanServices';
import ExerciseScenarioEditor from '@/components/ExerciseScenarioEditor';
import { buildScenarioBuilderAgentContext } from './scenarioBuilderContext';
import {
  isForesightClassification,
  normalizeScenarioBuilderPrefill,
} from './foresightClassification';

const LEVELS = ['Strategic', 'Tactical', 'Operational'];
const LEVEL_ICONS = { Strategic: Flag, Tactical: Crosshair, Operational: Wrench };

/* Premium dark shell (spec: Scenario Builder AGENT modal). Scoped `sba-` classes. */
const SBA_STYLE = `
@keyframes sbaModalIn { from { opacity: 0; transform: translateY(16px) scale(0.985); } to { opacity: 1; transform: none; } }
@keyframes sbaBubbleIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
.sba-font { font-family: 'IBM Plex Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
.sba-modal { animation: sbaModalIn 0.4s cubic-bezier(0.22,1,0.36,1); }
.sba-bubble { animation: sbaBubbleIn 0.3s ease; }
.sba-input {
  background: rgba(7,10,28,0.6); border: 1px solid rgba(150,165,210,0.2); border-radius: 11px;
  color: #fff; transition: border-color .15s, box-shadow .15s;
}
.sba-input::placeholder { color: #4a5378; }
.sba-input:focus { outline: none; border-color: #a855f7; box-shadow: 0 0 0 3px rgba(168,85,247,0.14); }
select.sba-input {
  appearance: none; -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a92b0' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 12px center;
}
.sba-primary {
  background: linear-gradient(135deg, #a855f7, #d946ef); color: #fff;
  box-shadow: 0 1px 0 rgba(255,255,255,0.2) inset, 0 8px 24px rgba(168,85,247,0.35);
  transition: filter .15s, transform .15s;
}
.sba-primary:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); }
`;

/* Header progress: 3-segment pill bar (done → violet 45%, active → AI gradient, todo → grey). */
const ProgressSegments = ({ step }) => (
  <span className="inline-flex items-center gap-1">
    {[1, 2, 3].map((s) => (
      <span
        key={s}
        className="h-1 w-[22px] rounded-full"
        style={{
          background: s < step ? 'rgba(168,85,247,0.45)'
            : s === step ? 'linear-gradient(135deg,#a855f7,#d946ef)'
            : 'rgba(150,165,210,0.2)',
        }}
      />
    ))}
  </span>
);

const DIFFICULTY_PILLS = [
  { name: 'Easy', sub: 'guided pace', Icon: CheckCircle2, tint: 'text-emerald-400' },
  { name: 'Medium', sub: 'realistic pressure', Icon: Gauge, tint: 'text-amber-400' },
  { name: 'Hard', sub: 'worst case', Icon: Flame, tint: 'text-red-400' },
];

const buildTimelinePayload = (item, createdIds) => {
  const actor = { id: 'ai-scenario-builder', name: 'Scenario Builder AGENT', type: 'system' };
  const g = item.group || 'status_update';
  const isCard = g === 'response_action' || g === 'pre_incident_action';
  const meta = {};

  if (item.fire_time_minutes != null && item.fire_time_minutes !== '') {
    meta.fireOffsetMinutes = Math.max(0, Number(item.fire_time_minutes) || 0);
  }

  // Roles for EVERY card type (verification, notification, status_update, resource — not only response actions).
  const rolesRaw = Array.isArray(item.roles) ? item.roles : [];
  if (rolesRaw.length) {
    meta.scenarioRoles = rolesRaw.map((r) => ({
      role: r.role || 'Responder',
      count: Number(r.count) || 1,
      instructions: r.instructions || '',
      sla: (r.sla_minutes ?? r.sla) != null ? String(r.sla_minutes ?? r.sla) : '',
    }));
  }

  if (isCard && Array.isArray(item._linkedResources) && item._linkedResources.length) {
    meta.linkedResources = item._linkedResources;
  }
  if (isCard) {
    meta.actionCardName = item.actionCardName || item.title || 'Action Card';
    const tasks = (item.tasks || []).map((t, i) => ({
      taskId: `task_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 7)}`,
      taskText: t.text || t.title || `Task ${i + 1}`,
      instructions: t.instructions || '',
      order: i,
      sla: Number(t.sla_minutes ?? t.sla) || 5,
      isCompleted: false,
      linkedResources: [],
    }));
    meta.tasks = tasks;
    // Card SLA is auto-calculated as the SUM of its task SLAs (matches the ResponseAction model).
    const taskSlaSum = tasks.reduce((s, t) => s + (Number(t.sla) || 0), 0);
    meta.sla = { withinMinutes: taskSlaSum || Number(item.sla_minutes) || 5 };
  } else if (g !== 'status_update' && item.sla_minutes) {
    meta.sla = { withinMinutes: Number(item.sla_minutes) || 0 };
  }
  if (g === 'resource') {
    const r = item.resource || {};
    meta.resource = {
      category: r.category || item.title || 'Resource',
      name: r.category || item.title || 'Resource',
      quantity: Number(r.quantity) || 1,
      cost: Number(r.cost) || 0,
      availability: 'once',
      assigneeType: '', assigneeId: '', assigneeName: '', assigneeParentId: '', roles: [],
    };
  }
  if (item.conditional && typeof item.conditional === 'object') {
    const c = item.conditional;
    const srcId = (createdIds && c.source_index != null) ? createdIds[c.source_index] : undefined;
    meta.conditional = {
      triggerType: c.trigger_type === 'completed' ? 'completed' : 'timeout',
      withinMinutes: Number(c.within_minutes) || Number(item.sla_minutes) || 10,
      ...(srcId ? { sourceItemId: srcId } : {}),
    };
  }
  return {
    group: g,
    actor,
    title: item.title || meta.actionCardName || 'Inject',
    details: item.details || '',
    chips: [],
    attachments: [],
    meta,
  };
};

const Bubble = ({ role, children }) => {
  const isUser = role === 'user';
  return (
    <div className={`sba-bubble flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`} data-testid={`sba-msg-${role}`}>
      <div
        className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-xl text-white"
        style={isUser
          ? { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(150,165,210,0.2)' }
          : { background: 'linear-gradient(135deg,#a855f7,#d946ef)', boxShadow: '0 6px 18px rgba(168,85,247,0.35)' }}
      >
        {isUser ? <User className="h-4 w-4" /> : <Bot className="h-[18px] w-[18px]" />}
      </div>
      <div
        className="max-w-[80%] whitespace-pre-wrap px-4 py-3 text-[15.5px] leading-relaxed"
        style={isUser
          ? { background: 'linear-gradient(180deg,rgba(168,85,247,0.22),rgba(217,70,239,0.14))',
              border: '1px solid rgba(168,133,255,0.25)', borderRadius: '18px 4px 18px 18px', color: '#efe9ff' }
          : { background: 'linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.03))',
              border: '1px solid rgba(150,165,210,0.16)', borderRadius: '4px 18px 18px 18px', color: '#e6e9f6',
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}
      >
        {children}
      </div>
    </div>
  );
};

const ScenarioBuilderAgent = ({ onClose, prefill = null }) => {
  const navigate = useNavigate();
  const normalizedPrefill = useMemo(
    () => normalizeScenarioBuilderPrefill(prefill || {}),
    [prefill]
  );
  const isDetectedPrefill = isForesightClassification(normalizedPrefill);
  const [phase, setPhase] = useState('setup'); // 'setup' | 'chat'
  const [name, setName] = useState(normalizedPrefill.name || '');
  const [level, setLevel] = useState(normalizedPrefill.level || 'Tactical');
  // ForeSight supplies the authoritative detected-case brief even though the
  // setup form intentionally does not expose a separate description field.
  const [description] = useState(normalizedPrefill.description || '');
  const [classification, setClassification] = useState(null);

  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [building, setBuilding] = useState(false);
  const scrollRef = useRef(null);

  const scrollToEnd = () => requestAnimationFrame(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  });

  const dedupeLines = (text) => {
    const lines = String(text || '').split('\n');
    const out = [];
    for (const ln of lines) {
      if (out.length && out[out.length - 1].trim() === ln.trim() && ln.trim() !== '') continue;
      out.push(ln);
    }
    return out.join('\n');
  };

  const send = async (text, { reset = false } = {}) => {
    setThinking(true);
    try {
      const res = await CommanServices.scenarioBuilderChat({ session_id: sessionId, message: text || '', reset });
      const data = res?.data || {};
      if (res?.status === false) { toast.error(data?.message || 'AI is unavailable'); return; }
      if (data.session_id) setSessionId(data.session_id);
      if (data.reply) {
        const clean = dedupeLines(data.reply);
        setMessages((m) => (reset ? [{ role: 'assistant', text: clean }] : [...m, { role: 'assistant', text: clean }]));
      }
      scrollToEnd();
      if (data.done && data.spec) await finalize(data.spec);
    } catch (e) {
      toast.error('Something went wrong talking to the agent');
    } finally {
      setThinking(false);
    }
  };

  const startInterview = async () => {
    if (!name.trim()) { toast.error('Please enter a scenario name'); return; }
    if (!classification || !classification.type_id) { toast.error('Please select at least an incident type & location'); return; }
    // Conditions must be unique per live scenario — fail fast before the interview.
    try {
      const res = await CommanServices.listScenarios();
      const locKey = (arr) => [...new Set((arr || []).filter(Boolean).map(String))].sort().join(',');
      const dup = (res?.data?.scenarios || []).find((s) =>
        s.status !== 'Archived' &&
        (s.type_id || '') === (classification.type_id || '') &&
        (s.event_id || '') === (classification.event_id || '') &&
        (s.trigger_id || '') === (classification.trigger_id || '') &&
        locKey(s.location) === locKey(classification.location)
      );
      if (dup) {
        toast.error(`A scenario with these exact conditions already exists: "${dup.name || 'Unnamed'}". Change the conditions or archive it first.`);
        return;
      }
    } catch (e) { /* backend enforces uniqueness at create */ }
    setPhase('chat');
    // Give the agent the FULL classification so the design stays focused on it.
    const ctx = buildScenarioBuilderAgentContext({
      name,
      level,
      classification,
      prefill: normalizedPrefill,
    });
    send(ctx, { reset: true });
  };

  const finalize = async (spec) => {
    setBuilding(true);
    try {
      const payload = {
        name: name.trim() || 'AI Scenario',
        // the description the planner and the agent agreed on in the chat —
        // it also becomes the report & detection card's Initial Description
        description: String(spec.description || description || '').trim(),
        level,
        ...(classification || {}),
        origin: 'ai_agent',
        ...(normalizedPrefill.source_warning_id ? {
          source_warning_id: normalizedPrefill.source_warning_id,
          source_evidence: normalizedPrefill.source_evidence || null,
        } : {}),
        assumptions: (spec.assumptions || []).map((a) => ({
          title: a.title || '', description: a.description || '', impact: a.impact || '', severity: a.severity || '',
        })),
        status: 'Draft',
      };
      const res = await CommanServices.createScenario(payload);
      if (res?.status === false) { toast.error(res?.message || 'Failed to create scenario'); return; }
      const scenario = res?.data || {};
      const incId = scenario.incident_id;
      const scenarioId = scenario.id;

      if (incId && spec.objective && (spec.objective.title || spec.objective.description || spec.objective.success_criteria)) {
        try {
          await CommanServices.updatePatchIncidents(incId, {
            simulation_objective: {
              title: (spec.objective.title || '').trim(),
              description: (spec.objective.description || '').trim(),
              success_criteria: (spec.objective.success_criteria || '').trim(),
            },
          });
        } catch (e) { /* objective optional */ }
      }

      // First pass: create every inject, collecting created ids by original index (for conditional wiring).
      const timeline = Array.isArray(spec.timeline) ? spec.timeline : [];
      const createdIds = new Array(timeline.length).fill(null);
      let failed = 0;
      for (let i = 0; i < timeline.length; i++) {
        try {
          const r = await CommanServices.addincidentTimeline(incId, buildTimelinePayload(timeline[i], createdIds));
          createdIds[i] = r?.data?.id || r?.data?._id || null;
        } catch (e) { failed++; }
      }
      // Wire resource links: every agent-created resource is linked to an
      // action card (rule: no resource stays unlinked). The agent declares
      // linked_action_index; fall back to the nearest preceding action card.
      const actionIndices = timeline
        .map((it, i) => (it?.group === 'response_action' ? i : null))
        .filter((i) => i != null);
      timeline.forEach((it, i) => {
        if (it?.group !== 'resource' || !createdIds[i]) return;
        let target = Number.isInteger(it.linked_action_index)
          && timeline[it.linked_action_index]?.group === 'response_action'
          ? it.linked_action_index : null;
        if (target == null) {
          target = [...actionIndices].reverse().find((a) => a < i) ?? actionIndices[0];
        }
        if (target == null || !createdIds[target]) return;
        const r = it.resource || {};
        (timeline[target]._linkedResources = timeline[target]._linkedResources || []).push({
          resourceId: createdIds[i],
          category: r.category || it.title || 'Resource',
          quantity: Number(r.quantity) || 1,
          availability: 'once',
          cost: Number(r.cost) || 0,
          note: '',
        });
      });

      // Second pass: patch cards that need conditional wiring or resource links
      // now that every created id is known.
      for (let i = 0; i < timeline.length; i++) {
        const c = timeline[i]?.conditional;
        const needsConditional = c && typeof c === 'object' && c.source_index != null && createdIds[c.source_index];
        const needsLinks = (timeline[i]?._linkedResources || []).length > 0;
        if (!createdIds[i] || (!needsConditional && !needsLinks)) continue;
        try {
          const payloadItem = buildTimelinePayload(timeline[i], createdIds);
          await CommanServices.updatePutIncidentTimeline(incId, createdIds[i], { ...payloadItem, id: createdIds[i] });
        } catch (e) { /* best-effort */ }
      }

      // Required Roles: every distinct role the agent used becomes a scenario
      // required role (candidates are picked later inside the card modals).
      try {
        const roleMap = {};
        timeline.forEach((it) => (it?.roles || []).forEach((r) => {
          const n = String(r?.role || '').trim();
          if (!n) return;
          roleMap[n] = Math.max(roleMap[n] || 1, Number(r?.count) || 1);
        }));
        if (scenarioId && Object.keys(roleMap).length) {
          await CommanServices.saveScenarioStaffingDefaults(scenarioId, {
            roles: Object.fromEntries(Object.entries(roleMap).map(([n, count]) => [
              n, { mode: 'fill_slots', required_count: count, preferred: [], fallbacks: [] },
            ])),
          });
        }
      } catch (e) { /* required roles can be refined later */ }

      if (failed > 0) toast.warning(`Scenario created — ${failed} inject(s) could not be added`);
      else toast.success('Scenario created — opening it to refine');
      onClose?.();
      if (scenarioId) navigate(`/scenario-library/${scenarioId}`);
    } catch (e) {
      toast.error('Failed to build the scenario');
    } finally {
      setBuilding(false);
    }
  };

  const handleSend = () => {
    const t = input.trim();
    if (!t || thinking || building) return;
    setMessages((m) => [...m, { role: 'user', text: t }]);
    setInput('');
    scrollToEnd();
    send(t);
  };

  // Quick-reply pills appear when the agent asks a choice question (difficulty).
  const lastMsg = messages[messages.length - 1];
  const canPill = phase === 'chat' && !thinking && !building && lastMsg?.role === 'assistant';
  const showDifficultyPills = canPill
    && /easy/i.test(lastMsg.text) && /medium/i.test(lastMsg.text) && /hard/i.test(lastMsg.text);
  // The agent listed 3 numbered scenario suggestions — let the planner pick one.
  const showOptionPills = canPill && !showDifficultyPills
    && /(^|\n)\s*1[.)]\s/.test(lastMsg.text) && /(^|\n)\s*2[.)]\s/.test(lastMsg.text) && /(^|\n)\s*3[.)]\s/.test(lastMsg.text);
  // The agent asked the planner to DESCRIBE the scenario — offer suggestions.
  const showSuggestPill = canPill && !showDifficultyPills && !showOptionPills
    && /describe/i.test(lastMsg.text);
  // "Draft it for me" pill whenever the agent offers to draft an answer
  // (objective / assumptions questions).
  const showDraftPill = canPill && !showDifficultyPills && !showOptionPills && !showSuggestPill
    && /objective|assumption|\bdraft\b|\bsuggest\b|\bpropose\b|default/i.test(lastMsg.text);
  const quickReply = (text) => {
    if (thinking || building) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    scrollToEnd();
    send(text);
  };

  // When the planner has said "finish"/"create it", the wait is the scenario
  // being generated — show a build banner instead of anonymous typing dots.
  const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
  const generatingScenario = thinking
    && lastUserMsg && /\b(finish|create it|ready|build it|go ahead|done)\b/i.test(lastUserMsg.text);

  const contextChip = useMemo(() => {
    const bits = [name.trim() || 'Untitled', level, classification?.exercise_classification].filter(Boolean);
    return bits.join(' · ');
  }, [name, level, classification]);

  return createPortal(
    <div
      className="sba-font fixed inset-0 z-[1200] flex items-center justify-center p-3 sm:p-6"
      style={{
        background:
          'radial-gradient(60% 50% at 20% 10%, rgba(124,58,237,0.18), transparent 60%), ' +
          'radial-gradient(50% 45% at 85% 85%, rgba(79,70,229,0.14), transparent 60%), rgba(2,4,14,0.72)',
        backdropFilter: 'blur(4px)',
      }}
      data-testid="scenario-builder-agent"
      onClick={onClose}
    >
      <style>{SBA_STYLE}</style>
      <div
        className="sba-modal relative flex w-full flex-col overflow-hidden"
        style={{
          maxWidth: 'min(720px, 100%)',
          height: phase === 'setup' ? 'min(90vh, 860px)' : 'min(820px, calc(100vh - 80px))',
          background: 'linear-gradient(180deg,#0d1128,#0a0e22)',
          border: '1px solid rgba(168,133,255,0.16)',
          borderRadius: 24,
          boxShadow: '0 1px 0 rgba(255,255,255,0.06) inset, 0 30px 80px rgba(0,0,0,0.6), 0 0 60px rgba(124,58,237,0.1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top edge gradient strip */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
          style={{ background: 'linear-gradient(90deg, transparent, #c084fc 30%, #7c3aed 70%, transparent)', opacity: 0.8 }}
        />
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{
            background: 'linear-gradient(90deg, rgba(124,58,237,0.1), rgba(40,173,227,0.04))',
            borderBottom: '1px solid rgba(150,165,210,0.12)',
          }}
        >
          <div className="flex items-center gap-3.5">
            <div
              className="flex h-[47px] w-[47px] items-center justify-center rounded-[14px]"
              style={{ background: 'linear-gradient(135deg,#a855f7,#d946ef)', boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 8px 24px rgba(168,85,247,0.4)' }}
            >
              <Wand2 className="h-[21px] w-[21px] text-white" />
            </div>
            <div className="leading-tight">
              <h2 className="text-[18.5px] font-bold text-white">Scenario Builder</h2>
              <p className="mt-1 flex items-center gap-2 text-[12.5px]" style={{ color: '#8a92b0' }}>
                <ProgressSegments step={phase === 'setup' ? 1 : 2} />
                {phase === 'setup' ? 'Step 1 of 3 — basics & classification' : 'Step 2 of 3 — AI designs your timeline'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full transition-colors"
            style={{ color: '#8a92b0', border: '1px solid rgba(150,165,210,0.16)', background: 'rgba(255,255,255,0.03)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#8a92b0'; }}
            data-testid="sba-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {phase === 'setup' ? (
          <>
            <div className="grid flex-1 content-start gap-[22px] overflow-y-auto no-scrollbar p-7" data-testid="sba-setup">
              <div>
                <label className="mb-1.5 block text-[11.5px] font-bold uppercase" style={{ letterSpacing: '0.12em', color: '#8a92b0' }}>
                  Scenario Name <span style={{ color: '#f87171' }}>*</span>
                </label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Warehouse Fire — Night Shift" data-testid="sba-name"
                  className="sba-input w-full px-3.5 py-2.5 text-sm" />
              </div>
              <div>
                <label className="mb-2.5 block text-[11.5px] font-bold uppercase" style={{ letterSpacing: '0.12em', color: '#8a92b0' }}>Scenario Level</label>
                <div className="flex flex-wrap gap-2" data-testid="sba-level">
                  {LEVELS.map((l) => {
                    const LevelIcon = LEVEL_ICONS[l];
                    const active = level === l;
                    return (
                      <button
                        key={l}
                        type="button"
                        onClick={() => setLevel(l)}
                        data-testid={`sba-level-pill-${l}`}
                        aria-pressed={active}
                        className="flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-semibold transition-all"
                        style={active
                          ? { background: 'linear-gradient(135deg,#a855f7,#d946ef)', color: '#fff',
                              boxShadow: '0 1px 0 rgba(255,255,255,0.2) inset, 0 6px 18px rgba(168,85,247,0.35)', border: '1px solid transparent' }
                          : { background: 'rgba(7,10,28,0.6)', color: '#aab2cc', border: '1px solid rgba(150,165,210,0.2)' }}
                      >
                        <LevelIcon className="h-4 w-4" style={active ? undefined : { color: '#c084fc' }} />
                        {l}
                      </button>
                    );
                  })}
                </div>
              </div>
              {isDetectedPrefill && (
                <div
                  className="grid gap-2 rounded-[13px] px-4 py-3"
                  style={{
                    background: 'rgba(14,116,144,0.08)',
                    border: '1px solid rgba(56,189,248,0.2)',
                  }}
                  data-testid="sba-prefill-summary"
                >
                  <span
                    className="text-[10px] font-black uppercase tracking-[0.13em]"
                    style={{ color: '#67e8f9' }}
                  >
                    ForeSight detected case
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    {[
                      ['Incident Type', normalizedPrefill.incident_type],
                      ['Event', normalizedPrefill.event],
                      ['Trigger', normalizedPrefill.trigger],
                    ].map(([label, value], index) => (
                      <React.Fragment key={label}>
                        {index > 0 && <span style={{ color: '#55607d' }}>→</span>}
                        <span
                          className="inline-grid gap-0.5 rounded-lg px-2.5 py-1.5"
                          style={{
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(150,165,210,0.16)',
                          }}
                        >
                          <small className="text-[8px] font-bold uppercase tracking-[0.08em]" style={{ color: '#7883a4' }}>
                            {label}
                          </small>
                          <b className="text-[11.5px]" style={{ color: '#e6e9f6' }}>
                            {value || 'Not specified'}
                          </b>
                        </span>
                      </React.Fragment>
                    ))}
                  </div>
                  <span className="text-[11.5px]" style={{ color: '#9ba6c6' }}>
                    Scenario Level: <b style={{ color: '#fff' }}>{normalizedPrefill.level}</b>
                    {' · '}Exercise Classification: <b style={{ color: '#fff' }}>{normalizedPrefill.exercise_classification || 'Not specified'}</b>
                    {normalizedPrefill.location_names.length > 0 && (
                      <> · Location: <b style={{ color: '#fff' }}>{normalizedPrefill.location_names.join(' · ')}</b></>
                    )}
                  </span>
                </div>
              )}
              <ExerciseScenarioEditor incident={normalizedPrefill} onChange={setClassification} dark />
            </div>
            <div
              className="flex items-center justify-between gap-4 px-7 py-4"
              style={{ borderTop: '1px solid rgba(150,165,210,0.12)', background: 'rgba(7,10,28,0.4)' }}
            >
              <p className="text-[12.5px]" style={{ color: '#5f6890' }}>The agent drafts the full scenario from these basics.</p>
              <button onClick={startInterview} data-testid="sba-continue"
                className="sba-primary flex items-center gap-2 rounded-xl px-5 py-2.5 text-[15px] font-bold">
                <Wand2 className="h-4 w-4" /> Design with AI <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </>
        ) : (
          <>
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto no-scrollbar px-6 py-5" data-testid="sba-messages">
              {/* Context chip */}
              <div className="flex justify-center">
                <span
                  className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[12.5px]"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(150,165,210,0.16)', color: '#8a92b0' }}
                >
                  <FileText className="h-3.5 w-3.5 text-sky-400" />
                  Designing from your basics — <strong style={{ color: '#cbd2e8' }}>{contextChip}</strong>
                </span>
              </div>
              {messages.map((m, i) => <Bubble key={i} role={m.role}>{String(m.text || '').replace(/\*\*/g, '')}</Bubble>)}
              {showDifficultyPills && (
                <div className="flex flex-wrap gap-2.5 pl-[50px]" data-testid="sba-difficulty-pills">
                  {DIFFICULTY_PILLS.map(({ name: dn, sub, Icon, tint }) => (
                    <button
                      key={dn}
                      type="button"
                      onClick={() => quickReply(dn)}
                      data-testid={`sba-pill-${dn.toLowerCase()}`}
                      className="flex items-center gap-2.5 rounded-2xl px-4 py-2.5 text-left transition-all hover:-translate-y-px"
                      style={{ background: 'rgba(7,10,28,0.6)', border: '1px solid rgba(150,165,210,0.2)' }}
                    >
                      <Icon className={`h-4 w-4 ${tint}`} />
                      <span className="leading-tight">
                        <span className="block text-[13.5px] font-bold text-white">{dn}</span>
                        <span className="block text-[11.5px]" style={{ color: '#8a92b0' }}>{sub}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {showSuggestPill && (
                <div className="flex pl-[50px]" data-testid="sba-suggest-pill-row">
                  <button
                    type="button"
                    onClick={() => quickReply("I'm not sure — please suggest 3 scenario descriptions to choose from.")}
                    data-testid="sba-suggest-pill"
                    className="flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-bold transition-all hover:-translate-y-px"
                    style={{ background: 'rgba(168,85,247,0.14)', border: '1px solid rgba(168,85,247,0.35)', color: '#d8b4fe' }}
                  >
                    <Sparkles className="h-4 w-4" /> Suggest 3 scenarios
                  </button>
                </div>
              )}
              {showOptionPills && (
                <div className="flex flex-wrap gap-2.5 pl-[50px]" data-testid="sba-option-pills">
                  {[1, 2, 3].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => quickReply(`Use suggestion ${n}.`)}
                      data-testid={`sba-option-pill-${n}`}
                      className="flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-bold transition-all hover:-translate-y-px"
                      style={{ background: 'rgba(7,10,28,0.6)', border: '1px solid rgba(150,165,210,0.2)', color: '#e6e9f6' }}
                    >
                      <CheckCircle2 className="h-4 w-4" style={{ color: '#c084fc' }} /> Use #{n}
                    </button>
                  ))}
                </div>
              )}
              {showDraftPill && (
                <div className="flex pl-[50px]" data-testid="sba-draft-pill-row">
                  <button
                    type="button"
                    onClick={() => quickReply('Yes — please draft it for me.')}
                    data-testid="sba-draft-pill"
                    className="flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-bold transition-all hover:-translate-y-px"
                    style={{ background: 'rgba(168,85,247,0.14)', border: '1px solid rgba(168,85,247,0.35)', color: '#d8b4fe' }}
                  >
                    <Sparkles className="h-4 w-4" /> Draft it for me
                  </button>
                </div>
              )}
              {thinking && (generatingScenario ? (
                <div className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm"
                  style={{ background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)', color: '#d8b4fe' }}
                  data-testid="sba-generating">
                  <Loader2 className="h-4 w-4 animate-spin" /> Scenario is building — designing the focused timeline…
                </div>
              ) : (
                <div className="flex items-center gap-3" data-testid="sba-thinking">
                  <div className="flex h-[38px] w-[38px] items-center justify-center rounded-xl text-white"
                    style={{ background: 'linear-gradient(135deg,#a855f7,#d946ef)', boxShadow: '0 6px 18px rgba(168,85,247,0.35)' }}>
                    <Bot className="h-[18px] w-[18px]" />
                  </div>
                  <span className="flex gap-1">
                    <span className="h-2 w-2 animate-bounce rounded-full" style={{ background: '#8a92b0', animationDelay: '0ms' }} />
                    <span className="h-2 w-2 animate-bounce rounded-full" style={{ background: '#8a92b0', animationDelay: '150ms' }} />
                    <span className="h-2 w-2 animate-bounce rounded-full" style={{ background: '#8a92b0', animationDelay: '300ms' }} />
                  </span>
                </div>
              ))}
              {building && (
                <div className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm"
                  style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7' }}
                  data-testid="sba-building">
                  <Loader2 className="h-4 w-4 animate-spin" /> Building your scenario &amp; injecting the timeline…
                </div>
              )}
            </div>
            <div className="px-5 pb-4 pt-3" style={{ borderTop: '1px solid rgba(150,165,210,0.12)', background: 'rgba(7,10,28,0.45)' }}>
              <p className="mb-2.5 flex items-center justify-center gap-1.5 text-[12px]" style={{ color: '#8a92b0' }}>
                <Sparkles className="h-3.5 w-3.5" style={{ color: '#a855f7' }} />
                Say <strong style={{ color: '#d8b4fe' }}>"finish"</strong> anytime to generate the scenario from what we have.
              </p>
              <div className="flex items-center gap-2.5">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  placeholder={building ? 'Creating your scenario…' : 'Type your answer… (Shift+Enter for a new line)'}
                  rows={1}
                  disabled={thinking || building}
                  data-testid="sba-input"
                  className="sba-input max-h-32 flex-1 resize-none px-5 py-3.5 text-sm disabled:opacity-60"
                  style={{ borderRadius: 999 }}
                />
                <button onClick={handleSend} disabled={!input.trim() || thinking || building} data-testid="sba-send"
                  className="sba-primary flex h-[50px] w-[50px] flex-shrink-0 items-center justify-center rounded-full active:scale-95 disabled:opacity-40">
                  {thinking ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ScenarioBuilderAgent;
